import { createMemo, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import postHogClient from "@/api/posthog";
import { Button } from "@/components/ui/button";
import { COOKIE_POLICY_URL } from "../constants/consent";
import {
  getDebugOverride,
  hasValidChoice,
  openPreferences,
  saveConsent,
  shouldShowBanner,
} from "../store/consent-store";

/**
 * Non-modal cookie consent banner. Mounted above the router outlet so it persists
 * across SPA navigation until the user chooses. Visibility is driven by the pure
 * `shouldShowBanner` predicate (env + QA override + whether a valid choice exists).
 *
 * A11y: `role="region"` + `aria-live="polite"` (announced, not an alertdialog),
 * no autofocus and no focus trap so a user arriving from a magic link never loses
 * focus and can read the page without choosing (SPEC AC 12, FLOW D9).
 */
export function CookieBanner() {
  const { t } = useI18n();

  // env and the QA override do not change during a session — read once.
  const envEnabled = postHogClient.getIsEnabled();
  const forceOverride = getDebugOverride();

  const visible = createMemo(() =>
    shouldShowBanner(envEnabled, hasValidChoice(), forceOverride),
  );

  const acceptAll = () => {
    saveConsent(true);
    postHogClient.enableCapture();
  };

  const rejectAll = () => {
    saveConsent(false);
    postHogClient.disableCapture();
  };

  return (
    <Show when={visible()}>
      <section
        aria-label={t("cookie_consent.banner.aria_label")}
        aria-live="polite"
        class="fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.06)]"
      >
        <div class="mx-auto max-w-7xl px-4 py-4 lg:px-8">
          <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div class="max-w-3xl">
              <p class="text-sm font-semibold text-gray-900">
                {t("cookie_consent.banner.title")}
              </p>
              <p class="mt-1 text-sm text-gray-600">
                {t("cookie_consent.banner.description")}{" "}
                <a
                  href={COOKIE_POLICY_URL}
                  target="_blank"
                  rel="noopener"
                  class="font-medium text-primary underline underline-offset-4"
                >
                  {t("cookie_consent.banner.policy_link")}
                </a>
              </p>
            </div>
            <div class="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center">
              <Button variant="link" size="sm" onClick={openPreferences}>
                {t("cookie_consent.banner.customize")}
              </Button>
              <Button variant="neutralOutline" size="sm" onClick={rejectAll}>
                {t("cookie_consent.banner.reject_all")}
              </Button>
              <Button variant="default" size="sm" onClick={acceptAll}>
                {t("cookie_consent.banner.accept_all")}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Show>
  );
}
